import {
  Auth0LocalProvider,
  type CanAccessFunction,
  ChangePassword,
  isFinanceUser,
  isManagerUser,
  Logout,
  ProtectedRoute,
  RouteError,
} from '@omnipresentgroup/auth';
import {
  hasFinanceScope,
  hasSuperManagerScope,
} from '@omnipresentgroup/helpers';
import { useFlags } from '@omnipresentgroup/launchdarkly';
import * as Sentry from '@sentry/react';
import { createBrowserRouter } from 'react-router';

import { environment } from '../environment';
import { App } from './app';
import { NotFound } from './components';
import * as constants from './constants';
import { useContractorOnly } from './hooks';
import {
  AddAdditionalPay,
  AddBulkSalaryChanges,
  AddContractorPage,
  AdditionalPay,
  AdditionalPayDetails,
  AddSalaryChange,
  BenefitsPage,
  ClientProfile,
  CompanyAddDocumentPage,
  CompanyDocuments,
  ContractorDetailsPage,
  ContractorInvoices,
  ContractorInvoicesPayment,
  ContractorInvoiceView,
  ContractorOnlyManagerPageNotAllowed,
  ContractorsListPage,
  ExpenseDetailsPage,
  ExpensesPage,
  Home,
  IntegrationsPage,
  InvoiceReportDetails,
  InvoiceReportEmployeeDetails,
  InvoiceReports,
  LeaveSettings,
  SalaryChanges,
  TimeOffsPage,
  Timesheet,
  XeroConfigurationPage,
} from './pages';
import {
  ContractualCompensation,
  EmployeeAddDocumentPage,
  EmployeeDocuments,
  EmployeeEmployments,
  EmployeesListPage,
} from './pages/Employees';
import { XeroErrorPage } from './pages/Integrations/XeroErrorPage';

const isManagerProtected: CanAccessFunction = (authUser) =>
  isManagerUser(authUser);

const isSuperManagerProtected: CanAccessFunction = (authUser) =>
  isManagerUser(authUser) && hasSuperManagerScope(authUser);

const isManagerWithFinanceScopeProtected: CanAccessFunction = (authUser) =>
  isManagerUser(authUser) && hasFinanceScope(authUser);

const isFinanceManagerProtected: CanAccessFunction = (authUser) =>
  isFinanceUser(authUser);

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  [
    {
      path: 'change-pw',
      element: (
        <ChangePassword apiUrl={environment.endpoints.OMNIPLATFORM_API} />
      ),
      errorElement: <RouteError />,
    },
    {
      path: constants.HOME_PATH,
      element: (
        <Auth0LocalProvider
          auth0Config={environment.auth0Config}
          basename={
            process.env.BRANCH_NAME ? `/${process.env.BRANCH_NAME}` : ''
          }
        >
          {/* Makes sure that the user is authenticated. */}
          <App />
        </Auth0LocalProvider>
      ),
      errorElement: <RouteError />,
      children: [
        /**
         * Home
         */
        {
          path: constants.HOME_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <Home />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.EMPLOYEES_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <EmployeesListPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.ADD_CONTRACTOR_PATH(),
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <AddContractorPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.CONTRACTORS_PATH,
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ContractorsListPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.CONTRACTOR_DETAILS_PATH(),
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ContractorDetailsPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.CONTRACTOR_INVOICES_PATH,
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ContractorInvoices />
            </ProtectedRoute>
          ),
        },
        {
          path: `${constants.CONTRACTOR_INVOICES_DETAILS_PATH()}`,
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ContractorInvoiceView />
            </ProtectedRoute>
          ),
        },
        {
          path: `${constants.CONTRACTOR_INVOICES_PAYMENT_PATH}`,
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ContractorInvoicesPayment />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.TIMESHEETS_PATH,
          Component: () => {
            const { enableTimesheetsArbeitszeit } = useFlags();
            return (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                {enableTimesheetsArbeitszeit ? <Timesheet /> : <NotFound />}
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.INVOICES_PATH,
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={(authUser) =>
                  isSuperManagerProtected(authUser) ||
                  isManagerWithFinanceScopeProtected(authUser)
                }
                noAccessFallback={<NotFound />}
              >
                <InvoiceReports />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.INVOICE_DETAILS_PATH(),
          Component: () => {
            const { enableInvoiceBreakdown } = useFlags();
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={(authUser) =>
                  isSuperManagerProtected(authUser) ||
                  isManagerWithFinanceScopeProtected(authUser)
                }
                noAccessFallback={<NotFound />}
              >
                {enableInvoiceBreakdown ? (
                  <InvoiceReportDetails />
                ) : (
                  <NotFound />
                )}
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.INVOICE_EMPLOYEE_DETAILS_PATH(),
          Component: () => {
            const { enableInvoiceBreakdown } = useFlags();
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={(authUser) =>
                  isSuperManagerProtected(authUser) ||
                  isManagerWithFinanceScopeProtected(authUser)
                }
                noAccessFallback={<NotFound />}
              >
                {enableInvoiceBreakdown ? (
                  <InvoiceReportEmployeeDetails />
                ) : (
                  <NotFound />
                )}
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.CLIENT_PROFILE_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <ClientProfile />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.LEAVE_SETTING_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <LeaveSettings />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.SALARY_CHANGES_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <SalaryChanges />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.ADD_SALARY_CHANGE_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <AddSalaryChange />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.ADD_BULK_SALARY_CHANGES_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <AddBulkSalaryChanges />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.TIME_PATH,
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <TimeOffsPage />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.CONTRACTUAL_COMPENSATION_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <ContractualCompensation />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.ADDITIONAL_PAY_PATH,
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <AdditionalPay />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.ADDITIONAL_PAY_DETAILS_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <AdditionalPayDetails />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.ADD_ADDITIONAL_PAY_PATH,
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <AddAdditionalPay />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.INTEGRATIONS_PATH,
          Component: () => {
            const { enableNewIntegration } = useFlags();

            return enableNewIntegration ? (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <IntegrationsPage />
              </ProtectedRoute>
            ) : (
              <NotFound />
            );
          },
        },
        {
          path: constants.BENEFITS_PATH,
          Component: () => {
            const { enableBenefits } = useFlags();

            return enableBenefits ? (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <BenefitsPage />
              </ProtectedRoute>
            ) : (
              <NotFound />
            );
          },
        },
        {
          path: constants.XERO_CONFIGURATION_PATH,
          element: (
            <ProtectedRoute
              canAccess={isSuperManagerProtected || isFinanceManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <XeroConfigurationPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.XERO_ERROR_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <XeroErrorPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.DOCUMENTS_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <CompanyDocuments />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.ADD_DOCUMENT_PATH,
          element: (
            <ProtectedRoute
              canAccess={isManagerProtected}
              noAccessFallback={<NotFound />}
            >
              <CompanyAddDocumentPage />
            </ProtectedRoute>
          ),
        },
        {
          path: constants.EMPLOYEE_DOCUMENTS_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <EmployeeDocuments />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.EMPLOYEE_ADD_DOCUMENT_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <EmployeeAddDocumentPage />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.EMPLOYEE_EMPLOYMENTS_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <EmployeeEmployments />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.EXPENSES_PATH,
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <ExpensesPage />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.EXPENSES_DETAILS_PATH(),
          Component: () => {
            const { isContractorOnly } = useContractorOnly();
            return isContractorOnly ? (
              <ContractorOnlyManagerPageNotAllowed />
            ) : (
              <ProtectedRoute
                canAccess={isManagerProtected}
                noAccessFallback={<NotFound />}
              >
                <ExpenseDetailsPage />
              </ProtectedRoute>
            );
          },
        },
        {
          path: constants.LOGOUT_PATH,
          element: <Logout />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ],
  { basename: process.env.BRANCH_NAME ? `/${process.env.BRANCH_NAME}` : '/' },
);
